import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getSafes,
  deleteSafe,
  toggleSafeStatus,
} from '../../../../redux/Slices/SafeSlice';
import { clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaPrint,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import { matchesActiveFilter } from '../../../../shared/utils.js/utils';
import usePdfExport from '../../../../shared/pdf/usePdfExport';
import PdfExportManager from '../../../../shared/pdf/PdfExportManager';
import { exportRowsToExcel } from '../../../../shared/utils.js/excelExport';

const SEARCH_DEBOUNCE_MS = 400;
const SORT_FIELDS = {
  INDEX: '__index',
  NAME: 'name',
  STATUS: 'is_active',
  DEFAULT: 'is_default',
};

const Safes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const {
    safes: reduxSafes,
    safesPagination,
    error,
    success,
  } = useSelector(state => state.auth);
  const [safes, setSafes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [safeToDelete, setSafeToDelete] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const pendingToggleRef = useRef(new Set());
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';

  // Sync local safes state with Redux state (but not during pending toggles)
  useEffect(() => {
    if (reduxSafes && pendingToggleRef.current.size === 0) {
      setSafes(reduxSafes);
    }
  }, [reduxSafes]);

  useEffect(() => {
    setTitle('sidenav.safes');
  }, [setTitle]);

  useEffect(() => {
    const params = { page: currentPage, per_page: perPage };
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    if (isActiveFilter !== '') params.is_active = isActiveFilter;
    dispatch(getSafes(params));
  }, [dispatch, currentPage, debouncedSearchTerm, isActiveFilter, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: true });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
      dispatch(
        getSafes({
          page: currentPage,
          per_page: perPage,
          search: debouncedSearchTerm || undefined,
          is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
        }),
      );
    }
  }, [success, dispatch, currentPage, debouncedSearchTerm, isActiveFilter, perPage]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setIsActiveFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePerPageInputChange = value => {
    const maxAllowed =
      safesPagination?.total || safesPagination?.per_page || safes?.length || 1000;
    const numericValue = Number(value);
    const safeValue = Number.isNaN(numericValue)
      ? 1
      : Math.min(Math.max(1, Math.floor(numericValue)), maxAllowed);
    setPerPageInput(safeValue);
  };

  const handleApplyPerPage = () => {
    const maxAllowed =
      safesPagination?.total || safesPagination?.per_page || safes?.length || 1000;
    const nextValue = Math.min(Math.max(1, Number(perPageInput) || 1), maxAllowed);
    setPerPage(nextValue);
    setPerPageInput(nextValue);
    setCurrentPage(1);
  };

  const handleDelete = safe => {
    setSafeToDelete(safe);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (safeToDelete) {
      await dispatch(deleteSafe(safeToDelete.id));
      setShowDeleteModal(false);
      setSafeToDelete(null);
    }
  };

  const handleToggleStatus = async id => {
    pendingToggleRef.current.add(id);
    const originalSafes = [...safes];
    setSafes(
      safes.map(safe =>
        safe.id === id ? { ...safe, is_active: !safe.is_active } : safe,
      ),
    );
    try {
      await dispatch(toggleSafeStatus(id)).unwrap();
      pendingToggleRef.current.delete(id);
    } catch (err) {
      pendingToggleRef.current.delete(id);
      if (err?.message !== 'OFFLINE_REQUEST_SAVED' && !err?.isOffline) {
        setSafes(originalSafes);
      }
      if (err?.message === 'OFFLINE_REQUEST_SAVED' || err?.isOffline) {
        toast.info(
          isRTL
            ? 'سيتم تحديث الحالة عند عودة الاتصال بالإنترنت'
            : 'Status will be updated when back online',
          { rtl: isRTL, toastId: `offline-toggle-${id}` },
        );
      }
    }
  };

  const getSafeName = safe => {
    if (!safe) return '—';
    return isRTL
      ? safe.name_ar || safe.name_en || safe.name || '—'
      : safe.name_en || safe.name_ar || safe.name || '—';
  };

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = field => {
    if (sortField !== field)
      return <FaSort style={{ opacity: 0.4, marginInlineStart: '4px' }} />;
    return sortOrder === 'asc' ? (
      <FaSortUp style={{ marginInlineStart: '4px' }} />
    ) : (
      <FaSortDown style={{ marginInlineStart: '4px' }} />
    );
  };

  const filteredSafes = useMemo(() => {
    let result = (safes || []).filter(safe => {
      if (!matchesActiveFilter(safe.is_active, isActiveFilter)) return false;
      if (!debouncedSearchTerm) return true;
      const q = debouncedSearchTerm.trim().toLowerCase();
      return (
        (safe.name_ar || '').toLowerCase().includes(q) ||
        (safe.name_en || '').toLowerCase().includes(q) ||
        (safe.name || '').toLowerCase().includes(q)
      );
    });
    if (sortField === SORT_FIELDS.INDEX) {
      return sortOrder === 'asc' ? result : [...result].reverse();
    }
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal = sortField === 'name' ? getSafeName(a) : (a[sortField] ?? '');
        let bVal = sortField === 'name' ? getSafeName(b) : (b[sortField] ?? '');
        if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
        if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
        const cmp = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
        });
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safes, debouncedSearchTerm, isActiveFilter, sortField, sortOrder, isRTL]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSafes.length / Math.max(1, perPage)),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedSafes = filteredSafes.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );
  const pageOffset = (currentPageSafe - 1) * perPage;

  // ---- Shared PDF / Excel column config -------------------------------
  const safeStatus = safe =>
    safe.is_active ? t('safes.active') : t('safes.inactive');
  const safeDefault = safe =>
    safe.is_default ? t('safes.default') : t('safes.notDefault');

  const exportColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('safes.tableHeaders.name'),
        render: getSafeName,
        excelValue: getSafeName,
      },
      {
        key: 'is_active',
        label: t('safes.tableHeaders.status'),
        render: safeStatus,
        excelValue: safeStatus,
      },
      {
        key: 'is_default',
        label: t('safes.tableHeaders.isDefault'),
        render: safeDefault,
        excelValue: safeDefault,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRTL],
  );

  const pdf = usePdfExport({
    idPrefix: 'safes',
    isRTL,
    fileNamePrefix: 'ACE_Safes',
    onError: err =>
      toast.error(
        err?.message === 'NO_DATA'
          ? t('safes.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' })
          : t('safes.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }),
        { rtl: isRTL },
      ),
  });

  const handleExportSafeToExcel = safe => {
    exportRowsToExcel({
      columns: exportColumns,
      rows: [safe],
      fileName: `${t('safes.excel.fileName', { defaultValue: 'Safe' })}_${getSafeName(safe)}`,
      sheetName: t('safes.excel.sheetName', { defaultValue: 'Safe' }),
      rtl: isRTL,
    });
    toast.success(
      t('safes.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }),
      { rtl: isRTL },
    );
  };

  const handleExportAllToExcel = () => {
    if (!filteredSafes || filteredSafes.length === 0) {
      toast.error(
        t('safes.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }),
        { rtl: isRTL },
      );
      return;
    }
    exportRowsToExcel({
      columns: exportColumns,
      rows: filteredSafes,
      fileName: t('safes.excel.fileName', { defaultValue: 'Safes' }),
      sheetName: t('safes.excel.sheetName', { defaultValue: 'Safes' }),
      startIndex: pageOffset,
      rtl: isRTL,
    });
    toast.success(
      t('safes.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }),
      { rtl: isRTL },
    );
  };

  return (
    <div
      style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '15px',
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, textAlign }}>
          {t('safes.title')}
        </h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => pdf.previewAll(filteredSafes)}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FaEye /> {t('safes.preview.all')}
          </button>
          <button
            onClick={() => pdf.printAll(filteredSafes)}
            disabled={pdf.isGeneratingPdf}
            style={{
              backgroundColor: pdf.isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: pdf.isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: pdf.isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint />{' '}
            {pdf.isGeneratingPdf
              ? t('safes.print.generating')
              : t('safes.print.all')}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FaFileExcel /> {t('safes.export.all')}
          </button>
          <button
            onClick={() => navigate('/safes/add')}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FaPlus /> {t('safes.add')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '15px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
          <FaSearch
            style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '16px',
            }}
          />
          <input
            type="text"
            placeholder={t('safes.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label={t('safes.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '12px 45px 12px 15px',
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
            }}
          />
        </div>
        <select
          value={isActiveFilter}
          onChange={handleFilterChange}
          aria-label={t('safes.allStatuses')}
          style={{
            padding: '12px 15px',
            backgroundColor: '#1b3350',
            border: '1px solid #333',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            minWidth: '150px',
            cursor: 'pointer',
          }}
        >
          <option value="">{t('safes.allStatuses')}</option>
          <option value="1">{t('safes.active')}</option>
          <option value="0">{t('safes.inactive')}</option>
        </select>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
            {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => handlePerPageInputChange(perPageInput - 1)}
              style={{
                backgroundColor: '#1b3350',
                color: 'white',
                border: '1px solid #333',
                padding: '6px 10px',
                borderRadius: '6px 0 0 6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                minWidth: '32px',
              }}
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max={safesPagination?.total || safes?.length || 1000}
              value={perPageInput}
              onChange={e => handlePerPageInputChange(e.target.value)}
              style={{
                width: '70px',
                padding: '6px 10px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderLeft: 'none',
                borderRight: 'none',
                borderRadius: 0,
                color: 'white',
                fontSize: '14px',
                textAlign: 'center',
              }}
            />
            <button
              onClick={() => handlePerPageInputChange(perPageInput + 1)}
              style={{
                backgroundColor: '#1b3350',
                color: 'white',
                border: '1px solid #333',
                padding: '6px 10px',
                borderRadius: '0 6px 6px 0',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                minWidth: '32px',
              }}
            >
              +
            </button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            {t('common.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
      </div>

      {filteredSafes && filteredSafes.length > 0 ? (
        <div
          style={{
            backgroundColor: '#1b3350',
            border: '1px solid #333',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: '920px',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  <th
                    onClick={() => handleSort(SORT_FIELDS.INDEX)}
                    style={{ ...thStyle, width: '60px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('safes.tableHeaders.serialNumber')}
                    {getSortIcon(SORT_FIELDS.INDEX)}
                  </th>
                  <th
                    onClick={() => handleSort(SORT_FIELDS.NAME)}
                    style={{ ...thStyle, width: '250px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('safes.tableHeaders.name')}
                    {getSortIcon(SORT_FIELDS.NAME)}
                  </th>
                  <th
                    onClick={() => handleSort(SORT_FIELDS.STATUS)}
                    style={{ ...thStyle, width: '100px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('safes.tableHeaders.status')}
                    {getSortIcon(SORT_FIELDS.STATUS)}
                  </th>
                  <th
                    onClick={() => handleSort(SORT_FIELDS.DEFAULT)}
                    style={{ ...thStyle, width: '120px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('safes.tableHeaders.isDefault')}
                    {getSortIcon(SORT_FIELDS.DEFAULT)}
                  </th>
                  <th style={{ ...thStyle, width: '200px' }}>
                    {t('safes.tableHeaders.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedSafes.map((safe, index) => (
                  <tr
                    key={safe.id}
                    style={{
                      borderBottom: '1px solid #333',
                      backgroundColor: index % 2 === 0 ? '#1b3350' : '#1a1f2e',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={ev =>
                      (ev.target.closest('tr').style.backgroundColor = '#2a3441')
                    }
                    onMouseLeave={ev =>
                      (ev.target.closest('tr').style.backgroundColor =
                        index % 2 === 0 ? '#1b3350' : '#1a1f2e')
                    }
                  >
                    <td style={tdStyle}>{pageOffset + index + 1}</td>
                    <td style={tdStyle}>{getSafeName(safe)}</td>
                    <td style={{ ...tdStyle, color: undefined }}>
                      <span
                        style={{
                          backgroundColor: safe.is_active ? '#AC2000' : '#dc3545',
                          color: 'white',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'inline-block',
                        }}
                      >
                        {safeStatus(safe)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: undefined }}>
                      <span
                        style={{
                          backgroundColor: safe.is_default ? '#28a745' : '#6c757d',
                          color: 'white',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'inline-block',
                        }}
                      >
                        {safeDefault(safe)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, minWidth: '200px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '8px',
                          flexWrap: 'nowrap',
                        }}
                      >
                        <ActionButton
                          title={t('safes.actions.view')}
                          onClick={() => pdf.previewSingle(safe)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t('safes.print.single')}
                          onClick={() => pdf.printSingle(safe, getSafeName(safe))}
                          disabled={pdf.isGeneratingPdf}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t('safes.export.single')}
                          onClick={() => handleExportSafeToExcel(safe)}
                          bg="#28a745"
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t('safes.actions.edit')}
                          onClick={() => navigate(`/safes/edit/${safe.id}`)}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t('safes.actions.delete')}
                          bg="#c0392b"
                          onClick={() => handleDelete(safe)}
                        >
                          <FaTrash />
                        </ActionButton>
                        <ActionButton
                          title={t('safes.actions.toggleStatus')}
                          bg="#34495e"
                          onClick={() => handleToggleStatus(safe.id)}
                        >
                          {safe.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 &&
            (() => {
              const pages = [];
              for (let i = 1; i <= totalPages; i += 1) {
                if (
                  i === 1 ||
                  i === totalPages ||
                  (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
                ) {
                  pages.push(i);
                } else if (
                  i === currentPageSafe - 3 ||
                  i === currentPageSafe + 3
                ) {
                  pages.push('...');
                }
              }
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    paddingBottom: '20px',
                    borderTop: '1px solid #333',
                    paddingTop: '20px',
                  }}
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                    disabled={currentPageSafe === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('safes.pagination.previous')}
                  </button>
                  {pages.map((page, idx) =>
                    page === '...' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{ padding: '8px', color: 'white' }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor:
                            page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                    }
                    disabled={currentPageSafe === totalPages}
                    style={{
                      padding: '8px 12px',
                      backgroundColor:
                        currentPageSafe === totalPages ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor:
                        currentPageSafe === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('safes.pagination.next')}
                  </button>
                </div>
              );
            })()}
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: '#888',
            backgroundColor: '#1b3350',
            borderRadius: '8px',
            border: '1px solid #333',
          }}
        >
          {t('safes.noData')}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
          }}
        >
          <div
            style={{
              backgroundColor: '#1a1f2e',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '400px',
              border: '1px solid #333',
            }}
          >
            <h2
              style={{
                color: 'white',
                marginBottom: '20px',
                fontSize: '20px',
                textAlign,
              }}
            >
              {t('safes.delete.title')}
            </h2>
            <p style={{ color: '#ccc', marginBottom: '30px', textAlign }}>
              {t('safes.delete.message')} "{getSafeName(safeToDelete)}"؟
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSafeToDelete(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t('safes.delete.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t('safes.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared PDF Engine — measurement, preview, print & single-record */}
      <PdfExportManager
        pdf={pdf}
        title={t('safes.title')}
        columns={exportColumns}
        rows={filteredSafes}
        numberingOffset={pageOffset}
      />
    </div>
  );
};

const thStyle = {
  padding: '18px 16px',
  textAlign: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '14px',
};

const tdStyle = {
  padding: '18px 16px',
  textAlign: 'center',
  color: 'white',
  fontSize: '14px',
  verticalAlign: 'middle',
};

const ActionButton = ({ children, onClick, title, bg = '#2f3644', disabled = false }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    aria-label={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 10px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '38px',
    }}
  >
    {children}
  </button>
);

export default Safes;
